import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ModulePackage, ModulePackagePricing, STANDALONE_BILLED_MODULE_TYPES} from '@app/interfaces/packages/module-package';
import {AccountModulePackage} from '@app/interfaces/packages/account-module-package';
import {ModulePackagesService} from '@app/services/api/module-packages.service';
import {ConfigAPIService} from '@app/services/api/config-api.service';
import {MatDialog} from '@angular/material/dialog';
import {NotificationService} from '@app/services/notification.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {LocaleEnum} from '@app/enums/LocaleEnum';
import {getCarPeakPricingUrl} from '@app/util/pricing-urls';
import {ActivateModulePackageDialogComponent} from '@app/shared/dialogs/activate-module-package-dialog/activate-module-package-dialog.component';
import {ConfirmDialogComponent, DialogType} from '@app/shared/dialogs';
import {CheckoutDialogComponent, CheckoutDialogResult} from '@app/shared/dialogs/checkout-dialog/checkout-dialog.component';
import {AccountFeaturesEnum} from '@app/interfaces/config/accountFeaturesEnum';
import {AvtonetInfoDialogComponent} from '@app/shared/dialogs/avtonet-info-dialog/avtonet-info-dialog.component';

@Component({
  selector: 'app-module-package-card',
  templateUrl: './module-package-card.component.html',
  styleUrls: ['./module-package-card.component.scss'],
  standalone: false
})
export class ModulePackageCardComponent implements OnInit, OnDestroy {

  @Input() modulePackage: ModulePackage;
  @Input() isActive: boolean = false;
  @Input() activeModulePackage: AccountModulePackage;
  @Input() hasConflict: boolean = false;
  @Input() conflictingModule: ModulePackage;
  @Input() isUpgrade: boolean = false;
  @Input() isDowngrade: boolean = false;

  @Output() moduleActivated = new EventEmitter<void>();
  @Output() moduleDeactivated = new EventEmitter<void>();

  public isLoading: boolean = false;
  public activePackageName: string = '';
  /** True when the account currently has an active, paid (non-trial, non-expired)
   *  Autobrief subscription. Consolidatable modules can only be activated then. */
  public hasActivePaidSubscription: boolean = false;
  /** True when the account is on an Autobrief trial. Consolidatable module
   *  activation stays disabled until they convert to a paid plan. */
  public isOnTrial: boolean = false;
  /** Free work-order units remaining before activation is required (work-order
   *  module only). Null when unknown/not applicable or already unlocked. */
  public freeUnitsRemaining: number | null = null;
  public freeUnitsLimit: number | null = null;
  public pricingUrl: string = '';
  public pricing: ModulePackagePricing | null = null;
  /** Locale-aware CarPeak marketing/pricing URL. Set once on init from the
   *  account config so the "Več na CarPeak" link points at the right
   *  language variant of the CarPeak site (SI → Cenik, HR → Cjenik, other
   *  → Pricing). Only used when this card represents a CarPeak module. */
  public carPeakUrl: string = '';

  private destroy$ = new Subject<void>();

  constructor(
    private modulePackagesService: ModulePackagesService,
    private configApi: ConfigAPIService,
    private matDialog: MatDialog,
    private notificationService: NotificationService
  ) {
  }

  ngOnInit(): void {
    this.configApi.getAccountConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe((config) => {
        const productPackage = config.activeAccountProductPackage;
        this.activePackageName = productPackage?.productPackage?.name || '';
        this.isOnTrial = !!productPackage?.isTrial;
        // Active paid subscription = a product package that exists and is neither
        // a trial nor expired. Mirrors the backend activation gate.
        this.hasActivePaidSubscription = !!productPackage && !productPackage.isTrial && !productPackage.isExpired;
        this.pricingUrl = this.getPricingUrl(config.locale);
        this.carPeakUrl = getCarPeakPricingUrl(config.locale);
      });

    // Load pricing tiers if module has subscription option
    if (this.modulePackage.hasSubscriptionOption) {
      this.loadPricing();
    }

    // Any module with a free-unit allowance: show how many remain before the
    // module must be activated. Modules whose type has no counter return null.
    if (this.modulePackage.includedUnits) {
      this.loadFreeUnitsQuota();
    }
  }

  /** Fetch the module's free-unit quota so the card can show "X of Y free left".
   *  Non-fatal: on error (or null data) the card falls back to the plain
   *  includedUnits label. */
  private loadFreeUnitsQuota(): void {
    this.modulePackagesService.getModuleQuota(this.modulePackage.uuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.freeUnitsLimit = res?.data?.limit ?? null;
          this.freeUnitsRemaining = res?.data?.remaining ?? null; // null once unlocked / no counter
        },
        error: () => {
          this.freeUnitsLimit = null;
          this.freeUnitsRemaining = null;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  activateModule(): void {
    if (this.modulePackage.moduleType === 'VIRTUAL_SALON') {
      this.activateVirtualSalonModule();
      return;
    }

    // Reuse the quota already loaded on this card so the dialog doesn't repeat
    // the (slow) request. Undefined for modules with no free-unit allowance —
    // the dialog then has nothing to show and won't fetch.
    const quota = this.modulePackage.includedUnits
      ? { remaining: this.freeUnitsRemaining, limit: this.freeUnitsLimit }
      : undefined;

    const dialogRef = ActivateModulePackageDialogComponent.open(
      this.matDialog,
      this.modulePackage,
      quota
    );

    // Real success signal: the confirm dialog closes itself (with no result)
    // before opening the checkout dialog, so its own afterClosed() can no
    // longer be trusted here. The component instance emits this once the
    // checkout dialog it opened actually reports success.
    dialogRef.componentInstance.activationSucceeded
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.moduleActivated.emit();
      });
  }

  /**
   * Virtual Salon skips the generic "Aktivacija modula" pre-step and opens the
   * checkout dialog directly for the tier the user clicked here — the same
   * CheckoutDialogComponent every other Virtual Salon upsell entry point
   * (hero, lead-generation, embed, report) already funnels through, so the
   * monthly/yearly picker for a first-time MINI purchase behaves identically
   * everywhere instead of duplicating that logic in a second dialog (BRIEFD-6162).
   */
  private activateVirtualSalonModule(): void {
    this.configApi.getAccountConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe((config) => {
        // Mirrors virtual-salon-hero.component.ts's `!isBasicActive && !isProActive`
        // guard: an account that already has real access must never be soft-gated
        // out of managing that access just because the coming-soon flag is also
        // set on their product package.
        const hasExistingAccess = !!config.accountFeatureFlags?.[AccountFeaturesEnum.HAS_VIRTUAL_SALON_BASIC]
          || !!config.accountFeatureFlags?.[AccountFeaturesEnum.HAS_VIRTUAL_SALON_PRO];
        if (!hasExistingAccess && config.accountFeatureFlags?.[AccountFeaturesEnum.HAS_VIRTUAL_SALON_COMING_SOON]) {
          AvtonetInfoDialogComponent.open(this.matDialog, {
            title: $localize`:@@advertising.virtual-salon.hero.coming-soon.title:Spletna stran kmalu na voljo`,
            intro: $localize`:@@advertising.virtual-salon.hero.coming-soon.intro:Ta funkcionalnost bo kmalu na voljo tudi za vaš račun. Obveščali te bomo, ko bo pripravljena.`,
            steps: [],
          });
          return;
        }

        const checkoutRef = CheckoutDialogComponent.open(this.matDialog, {
          type: 'module_package',
          modulePackage: this.modulePackage,
          hasSubscription: this.modulePackage.hasSubscriptionOption
        });

        checkoutRef.afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe((result: CheckoutDialogResult) => {
            if (result?.success && result?.activated) {
              this.moduleActivated.emit();
            }
          });
      });
  }

  deactivateModule(): void {
    // BRIEFD-7150: the two billing models need different promises. A consolidated
    // module was charged up front, so access and billing genuinely run to the end
    // of the paid period. A standalone/PAYG module (CarPeak) bills in ARREARS —
    // access ends immediately and a final invoice for the days used is issued — so
    // the period-end dialog would be wrong on all three of its claims.
    const type = this.modulePackage?.moduleType;
    const isArrearsBilled = !!type && STANDALONE_BILLED_MODULE_TYPES.includes(type);

    const dialogRef = ConfirmDialogComponent.open(
      this.matDialog,
      isArrearsBilled
        ? DialogType.DEACTIVATE_MODULE_PACKAGE_IMMEDIATE
        : DialogType.DEACTIVATE_MODULE_PACKAGE,
      false
    );

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        // ConfirmDialogComponent returns [true, checkCustom] or false
        const confirmed = Array.isArray(result) ? result[0] : result;

        if (confirmed && this.activeModulePackage) {
          this.isLoading = true;

          this.modulePackagesService.deactivateModulePackage(this.activeModulePackage.uuid)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.notificationService.showSuccessAlert($localize`Modul uspešno deaktiviran`);
                this.moduleDeactivated.emit();
                this.isLoading = false;
              },
              error: (error) => {
                console.error('Error deactivating module:', error);
                this.notificationService.showErrorAlert($localize`Napaka pri deaktivaciji modula`);
                this.isLoading = false;
              }
            });
        }
      });
  }

  getFormattedPrice(): string {
    if (this.modulePackage.isFree) {
      return $localize`Brezplačno`;
    }

    // CarPeak's real price depends on the account's Autobrief package and any
    // negotiated volume deal (BRIEFD-7435), so it comes from the pricing endpoint —
    // modulePackage.pricePerUnit is only the module row's static list value.
    if (this.pricing) {
      return `${(this.pricing.volume_per_vehicle ?? this.pricing.per_vehicle).toFixed(2)} €`;
    }

    if (this.modulePackage.pricePerUnit) {
      return `${this.modulePackage.pricePerUnit.toFixed(2)} €`;
    }

    if (this.isActive && this.activePeriod === 'yearly' && this.modulePackage.priceYearly != null) {
      return `${this.modulePackage.priceYearly.toFixed(2)} €`;
    }

    if (this.modulePackage.priceMonthly != null) {
      return `${this.modulePackage.priceMonthly.toFixed(2)} €`;
    }

    if (this.modulePackage.priceYearly != null) {
      return `${this.modulePackage.priceYearly.toFixed(2)} €`;
    }

    return $localize`Po povpraševanju`;
  }

  /** True when the header price reflects period-based billing (monthly/yearly)
   *  rather than a per-unit price — used to show a "/mes" or "/leto" suffix. */
  get isPeriodPrice(): boolean {
    return !this.modulePackage.isFree && !this.modulePackage.pricePerUnit && this.hasPeriodPricing;
  }

  getPeriodPriceLabel(): string {
    if (this.isActive && this.activePeriod === 'yearly') {
      return $localize`na leto`;
    }

    return $localize`na mesec`;
  }

  getBillingUnitLabel(): string {
    if (!this.modulePackage.billingUnit) {
      return '';
    }

    const labels: Record<string, string> = {
      'per_month': $localize`na mesec`,
      'per_vehicle': $localize`na vozilo`,
    };

    return labels[this.modulePackage.billingUnit] || this.modulePackage.billingUnit;
  }

  /** True when the module offers period-based billing (monthly and/or yearly). */
  get hasPeriodPricing(): boolean {
    return this.modulePackage?.priceMonthly != null || this.modulePackage?.priceYearly != null;
  }

  /** True when this module is billed by consolidating onto the account's
   *  Autobrief subscription (everything except the standalone-billed types
   *  in STANDALONE_BILLED_MODULE_TYPES). Such modules require an active paid
   *  subscription to activate. */
  get isConsolidatableModule(): boolean {
    const type = this.modulePackage?.moduleType;
    return !!type && !STANDALONE_BILLED_MODULE_TYPES.includes(type);
  }

  /** True when activation must be blocked and an upsell shown: a consolidatable
   *  module without an active paid subscription (none, expired, or on trial). */
  get requiresPaidSubscription(): boolean {
    return this.isConsolidatableModule && !this.hasActivePaidSubscription;
  }

  /** Billing period of the user's currently-active subscription, if any. */
  get activePeriod(): 'monthly' | 'yearly' | null {
    const p = (this.activeModulePackage as any)?.billingPeriod
      ?? (this.activeModulePackage as any)?.billing_period
      ?? null;
    return p === 'monthly' || p === 'yearly' ? p : null;
  }

  /** Computed yearly savings vs paying 12× the monthly price. */
  get yearlySavings(): number | null {
    const m = this.modulePackage?.priceMonthly;
    const y = this.modulePackage?.priceYearly;
    if (m == null || y == null) { return null; }
    const saved = (m * 12) - y;
    return saved > 0 ? saved : null;
  }

  loadPricing(): void {
    this.modulePackagesService.getModulePricing(this.modulePackage.uuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.pricing = result.data ?? null;
        },
        error: (error) => {
          console.error('Error loading pricing:', error);
        }
      });
  }

  private getPricingUrl(locale: LocaleEnum): string {
    const urls: Record<string, string> = {
      [LocaleEnum.LOCALE_SI]: 'https://autobrief.io/cenik/',
      [LocaleEnum.LOCALE_HR]: 'https://autobrief.io/hr/cjenik/',
    };

    return urls[locale] || 'https://autobrief.io/en/prices/';
  }

  /** Marker substring inside CarPeak module descriptions that the FE
   *  turns into a link in the top blurb and strips entirely from the
   *  green-check feature box. Matches the exact phrase the BE seeders
   *  store. */
  private static readonly CAR_PEAK_MARKER = 'Več na CarPeak';

  /**
   * Split the BE description around the "Več na CarPeak" marker. The top
   * description blurb renders `before + <a>marker</a> + after` so only the
   * phrase "Več na CarPeak" is a link; the surrounding sentence stays
   * plain text. Falls back to a no-link shape when the marker is missing
   * (which makes the template robust if a translator drops the marker).
   */
  get carPeakDescriptionParts(): {before: string; after: string; hasLink: boolean} {
    const desc = this.modulePackage?.description ?? '';
    const idx = desc.indexOf(ModulePackageCardComponent.CAR_PEAK_MARKER);
    if (idx < 0) {
      return {before: desc, after: '', hasLink: false};
    }
    return {
      before: desc.substring(0, idx),
      after: desc.substring(idx + ModulePackageCardComponent.CAR_PEAK_MARKER.length),
      hasLink: true,
    };
  }

  /**
   * Description with the ". Več na CarPeak." trailer removed for the
   * green-check feature box. We don't want the "Več na CarPeak" phrase
   * (linked or otherwise) to repeat between the top blurb and the
   * feature list — features should be the bare descriptive sentence.
   */
  get featureDescription(): string {
    const desc = this.modulePackage?.description ?? '';
    return desc
      .replace(/\.?\s*Več na CarPeak\.?\s*$/, '')
      .trim();
  }

  /** True when this card represents one of the CarPeak modules. Used by
   *  the template to swap the description / feature copy for the
   *  "Več na CarPeak" link. */
  get isCarPeakModule(): boolean {
    const name = this.modulePackage?.name ?? '';
    return /carpeak/i.test(name);
  }
}
